import { FeatureFlagTypes, ObjectTypes } from '@generated/graphql';
import type { CustomField } from '@interfaces/customField';
import { useHelpTextTokens } from '@ovComponents/2-component/helpText/helpText.tokens';
import InfoDialog from '@ovComponents/2-component/infoDialog/infoDialog';
import { TextField } from '@ovComponents/2-component/textField/textField';
import { useCustomFieldRenderer } from '@ovComponents/3-pattern/customField/useCustomFieldRenderer';
import { SelectionTile } from '@ovComponents/3-pattern/selectionTile/selectionTile';
import { additionalInfo } from '@ovComponents/4-module/configurableOptionFields/additionalInfo';
import { FieldRequiredType } from '@ovComponents/4-module/configurableOptionFields/basicField';
import { WorkflowCompletionForm } from '@ovComponents/4-module/workflowCompletion/components/workflowCompletionForm';
import { AnalyticsContext } from '@providers/analyticsProvider';
import { ovAnalyticsEvents } from '@utils/analytics/analytics';
import { Box, Grid, Typography, useTheme } from 'hephaestus/web';
import { kebabCase } from 'lodash-es';
import { useContext, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { useLocalization } from '../../../../../hooks/useLocalization';
import { useOrganizationFeatureFlag } from '../../../../../hooks/useOrganizationFeatureFlag';
import { buildEnrichedCustomOptions, invalidFields } from '../utils';
import { HouseholdSelection } from './components/householdSelection';

export const EditGoalVisual = ({
  options,
  goalData,
  loading,
  updateGoal,
  continueFunc,
  grid = false,
  updateMode = false,
  activeCustomFields,
  derivedCustomFields,
  workflowCompletion,
  ignoreCustomFieldTriggerRules,
  userId,
}: {
  options: any;
  goalData: any;
  loading: boolean;
  updateGoal: any;
  continueFunc: any;
  grid?: boolean;
  updateMode?: boolean;
  activeCustomFields?: string[];
  derivedCustomFields?: CustomField[];
  workflowCompletion?: any;
  ignoreCustomFieldTriggerRules?: boolean;
  userId?: string;
}) => {
  const { translateBackendString } = useLocalization();
  const theme = useTheme();
  const helpTextTokens = useHelpTextTokens(theme);
  const { sendAnalytic } = useContext(AnalyticsContext);
  const { t } = useTranslation(['client', 'shared']);
  const [focused, setFocused] = useState<string[]>([]);
  const [updated, setUpdated] = useState<boolean>(false);
  const hasHouseholdFlag = useOrganizationFeatureFlag(
    FeatureFlagTypes.Household,
  );
  const [customOptionSelected, setCustomOptionSelected] = useState<any[]>([]);
  const { renderCustomFields, validateAllCustomFields } =
    useCustomFieldRenderer({
      customOptions: customOptionSelected,
      customFieldValues: goalData.customFields,
    });

  const shouldDisplayInDialog = helpTextTokens.displayInDialog;

  const submit = async () => {
    const fields = invalidFields(
      {
        ...options,
      },
      goalData,
    );

    const invalidCustomFieldKeys = validateAllCustomFields();
    fields.push(...invalidCustomFieldKeys);
    setFocused(fields);
    if (fields.length === 0) {
      sendAnalytic(ovAnalyticsEvents.workflowsEditGoalContinueButtonSelect, {
        workflowStepTitle: options?.title,
        workflowId: workflowCompletion?.workflow?.id,
        workflowName: workflowCompletion?.workflow?.name,
        activeWorkflowCompletionId: workflowCompletion?.id,
        objectId: workflowCompletion?.objectId,
        objectType: workflowCompletion?.objectType,
      });
      await continueFunc();
    }
  };

  const update = (newValue: any) => {
    setUpdated(true);
    updateGoal(newValue);
  };

  useEffect(() => {
    setCustomOptionSelected(
      buildEnrichedCustomOptions(
        options,
        activeCustomFields,
        derivedCustomFields,
      ),
    );
  }, [options, activeCustomFields, derivedCustomFields]);

  const enableHousehold: boolean = hasHouseholdFlag;
  const defaultLinkToHousehold =
    (enableHousehold && options?.defaultLinkToHousehold) ?? false;

  return (
    <WorkflowCompletionForm
      onSubmit={submit}
      testId='edit-goal-form'
      submitButtonProps={{
        label: t(updateMode ? 'update' : 'continue'),
        disabled: loading || (!updated && updateMode),
        dataTestId: `edit-goal-${kebabCase(translateBackendString(options.title))}-continue-button`,
      }}
    >
      {options?.title && (
        <Typography variant='displayLarge' sx={{ mt: 1, mb: 3 }}>
          {translateBackendString(options?.title)}
          <Box
            sx={{
              display: 'inline-flex',
            }}
          >
            {additionalInfo(
              translateBackendString,
              options?.title?.additionalInfo,
            ) && (
              <InfoDialog
                information={
                  additionalInfo(
                    translateBackendString,
                    options?.title?.additionalInfo,
                  ) ?? ''
                }
              />
            )}
          </Box>
        </Typography>
      )}
      {options?.subtitle && (
        <Typography
          variant='bodyLarge'
          component={'div'}
          sx={{
            mb: 3,
            table: { width: '100%' },
            display: 'flex',
            alignItems: 'end',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              flexDirection: 'column',
              m: 0,
              '& > *': { m: 0 },
            }}
          >
            <ReactMarkdown linkTarget='_blank' remarkPlugins={[remarkGfm]}>
              {translateBackendString(options?.subtitle)}
            </ReactMarkdown>
          </Box>
          {additionalInfo(
            translateBackendString,
            options?.subtitle?.additionalInfo,
          ) && (
            <InfoDialog
              information={
                additionalInfo(
                  translateBackendString,
                  options?.subtitle?.additionalInfo,
                ) ?? ''
              }
            />
          )}
        </Typography>
      )}
      <Grid container spacing={2}>
        {options?.name?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='goal-name'
              onChange={(e: any) =>
                update({ ...goalData, name: e.target.value })
              }
              disabled={loading}
              label={translateBackendString(options?.name?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.name?.additionalInfo,
              )}
              fullWidth
              sx={{ mr: 2 }}
              value={goalData.name}
              onBlur={() => setFocused([...focused, 'name'])}
              error={
                !goalData.name &&
                focused.includes('name') &&
                options?.name?.required !== FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.targetAmountCents?.enabled && (
          <Grid size={{ xs: 12, md: grid ? 4 : 12 }}>
            <TextField
              testId='goal-target-amount'
              type='dollar'
              onChange={(e: any) =>
                update({ ...goalData, targetAmountCents: e.target.valueCents })
              }
              disabled={loading}
              label={translateBackendString(options?.targetAmountCents?.label)}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.targetAmountCents?.additionalInfo,
              )}
              fullWidth
              sx={{ mt: 2, mr: 2 }}
              value={goalData.targetAmountCents / 100}
              leadingIcon='dollar'
              onBlur={() => setFocused([...focused, 'targetAmountCents'])}
              error={
                !goalData.targetAmountCents &&
                focused.includes('targetAmountCents') &&
                options?.targetAmountCents?.required !==
                  FieldRequiredType.NOT_REQUIRED
              }
            />
          </Grid>
        )}
        {options?.timeHorizon?.enabled && options?.options ? (
          <Grid size={12}>
            <Typography
              variant='labelSmall'
              component='div'
              sx={{ color: theme.color.onSurfaceVariant, mb: 1, mt: 2 }}
            >
              {translateBackendString(options?.timeHorizon?.label)}
              {additionalInfo(
                translateBackendString,
                options?.timeHorizon?.additionalInfo,
              ) &&
                shouldDisplayInDialog && (
                  <InfoDialog
                    information={
                      additionalInfo(
                        translateBackendString,
                        options?.timeHorizon?.additionalInfo,
                      ) ?? ''
                    }
                  />
                )}
            </Typography>
            <SelectionTile
              testId='goal-time-horizon'
              onChange={(e: any) =>
                updateGoal({ ...goalData, timeHorizon: e.target.value })
              }
              value={goalData.timeHorizon}
              options={options?.options?.map((option: any) => ({
                value: option.value,
                label: translateBackendString(option.label),
              }))}
              error={
                !goalData.timeHorizon &&
                focused.includes('timeHorizon') &&
                options?.timeHorizon?.required
              }
              infoTooltip={
                additionalInfo(
                  translateBackendString,
                  options?.timeHorizon?.additionalInfo,
                ) && !shouldDisplayInDialog
                  ? additionalInfo(
                      translateBackendString,
                      options?.timeHorizon?.additionalInfo,
                    )
                  : undefined
              }
            />
          </Grid>
        ) : (
          options?.timeHorizon?.enabled &&
          !options?.options && (
            <Grid size={12}>
              <Typography
                variant='labelSmall'
                component='div'
                sx={{ color: theme.color.onSurfaceVariant, mb: 1, mt: 2 }}
              >
                {t('client:timeHorizon')}
              </Typography>
              <SelectionTile
                testId='goal-time-horizon'
                onChange={(e: any) =>
                  updateGoal({ ...goalData, timeHorizon: e.target.value })
                }
                value={goalData.timeHorizon}
                options={[
                  {
                    value: 'VERY_SHORT_TERM',
                    label: t('timeHorizonOptions.VERY_SHORT_TERM'),
                  },
                  {
                    value: 'SHORT_TERM',
                    label: t('timeHorizonOptions.SHORT_TERM'),
                  },
                  {
                    value: 'MID_TERM',
                    label: t('timeHorizonOptions.MID_TERM'),
                  },
                  {
                    value: 'LONG_TERM',
                    label: t('timeHorizonOptions.LONG_TERM'),
                  },
                ]}
                error={
                  !goalData.timeHorizon &&
                  focused.includes('timeHorizon') &&
                  options?.timeHorizon?.required !==
                    FieldRequiredType.NOT_REQUIRED
                }
                errorText={t('components:fieldErrors.selectionTile')}
              />
            </Grid>
          )
        )}
        {defaultLinkToHousehold && (
          <Grid
            sx={{
              mb: 1,
              mt: 2,
              mr: 2,
            }}
            size={{ xs: 12, md: 12 }}
          >
            <HouseholdSelection
              data-testid='house-hold-select'
              data={goalData}
              infoTooltip={additionalInfo(
                translateBackendString,
                options?.defaultLinkToHousehold?.additionalInfo,
              )}
              updateData={updateGoal}
              objectType={ObjectTypes.Goal}
            />
          </Grid>
        )}
        {customOptionSelected && (
          <Grid size={12}>
            {renderCustomFields({
              customData: goalData,
              update,
              grid,
              focused,
              setFocused,
              loading,
              ignoreCustomFieldTriggerRules,
              userId: userId ?? '',
            })}
          </Grid>
        )}
      </Grid>
    </WorkflowCompletionForm>
  );
};

export default EditGoalVisual;
